import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Banknote, TrendingDown } from "lucide-react";

const CURRENCIES = ["GBP", "USD", "EUR"];

const fmt = (v, cur) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, notation: "compact", maximumFractionDigits: 1 }).format(v || 0);
const fmtFull = (v, cur) =>
  new Intl.NumberFormat("en-GB", { style: "currency", currency: cur, maximumFractionDigits: 0 }).format(v || 0);

function ExceedanceCurve({ points, color }) {
  if (!points || points.length < 2) return null;
  const w = 520, h = 150, pad = 4;
  const maxLoss = Math.max(...points.map((p) => p.loss)) || 1;
  const x = (loss) => pad + (loss / maxLoss) * (w - 2 * pad);
  const y = (prob) => pad + (1 - prob) * (h - 2 * pad);
  const sorted = [...points].sort((a, b) => a.loss - b.loss);
  const line = sorted.map((p) => `${x(p.loss)},${y(p.probability)}`).join(" ");
  const area = `${x(sorted[0].loss)},${h} ${line} ${x(sorted[sorted.length - 1].loss)},${h}`;
  return (
    <svg viewBox={`0 0 ${w} ${h}`} className="h-36 w-full" data-testid="fair-exceedance-curve">
      <polygon points={area} fill={color} opacity="0.08" />
      <polyline points={line} fill="none" stroke={color} strokeWidth="2.5" strokeLinejoin="round" />
    </svg>
  );
}

export const FinancialExposure = () => {
  const [currency, setCurrency] = useState("GBP");
  const [data, setData] = useState(null);

  useEffect(() => {
    let active = true;
    api.get(`/fair/portfolio?currency=${currency}`)
      .then(({ data }) => active && setData(data))
      .catch(() => {});
    return () => {
      active = false;
    };
  }, [currency]);

  return (
    <section className="mt-10" data-testid="financial-exposure-section">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h2 className="ek-eyebrow flex items-center gap-1">
            <Banknote size={12} /> Financial exposure · Open FAIR
          </h2>
          <p className="mt-1 text-sm text-ek-ink/65">
            Annualised loss exposure across the portfolio, quantified with Monte-Carlo simulation.
          </p>
        </div>
        <div className="flex items-center gap-1 rounded-md border border-black/10 bg-white p-0.5" data-testid="fair-currency-switch">
          {CURRENCIES.map((c) => (
            <button
              key={c}
              onClick={() => setCurrency(c)}
              data-testid={`fair-currency-${c}`}
              className={`rounded px-3 py-1 text-xs font-semibold transition-colors ${
                currency === c ? "bg-ek-ink text-white" : "text-ek-ink/65 hover:text-ek-ink"
              }`}
            >
              {c}
            </button>
          ))}
        </div>
      </div>

      {data && (
        <div className="mt-4 grid gap-4 lg:grid-cols-3">
          {/* Headline ALE */}
          <div className="rounded-md border border-black/5 bg-white p-6" data-testid="fair-headline">
            <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">Expected annual loss</p>
            <p className="mt-1 font-display text-4xl font-bold text-ek-ink" data-testid="fair-mean-ale">
              {fmt(data.summary.mean, currency)}
            </p>
            <p className="mt-1 text-xs text-ek-ink/65">{fmtFull(data.summary.mean, currency)} mean ALE</p>
            <div className="mt-4 grid grid-cols-2 gap-3 border-t border-black/5 pt-4 text-sm">
              <div>
                <p className="text-xs text-ek-ink/65">P90 (worst-case)</p>
                <p className="font-semibold text-ek-ink">{fmt(data.summary.p90, currency)}</p>
              </div>
              <div>
                <p className="text-xs text-ek-ink/65">P50 (median)</p>
                <p className="font-semibold text-ek-ink">{fmt(data.summary.p50, currency)}</p>
              </div>
            </div>
          </div>

          {/* Exceedance curve */}
          <div className="rounded-md border border-black/5 bg-white p-6 lg:col-span-2">
            <div className="flex items-center justify-between">
              <p className="text-xs font-medium uppercase tracking-wide text-ek-ink/65">Loss exceedance curve</p>
              <span className="text-[11px] text-ek-ink/35">probability of exceeding a given annual loss</span>
            </div>
            <ExceedanceCurve points={data.exceedance} color="#6d28d9" />
            <div className="mt-3 space-y-1.5 border-t border-black/5 pt-3">
              <p className="mb-1 flex items-center gap-1 text-xs font-medium text-ek-ink/65">
                <TrendingDown size={12} /> Top financial contributors
              </p>
              {data.contributions.slice(0, 4).map((c) => (
                <div key={c.risk_id} className="flex items-center justify-between gap-2 text-xs" data-testid={`fair-contributor-${c.risk_id}`}>
                  <span className="truncate text-ek-ink/70">{c.title}</span>
                  <span className="shrink-0 font-semibold text-ek-ink">{fmt(c.mean_ale, currency)}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
      {!data && <p className="mt-4 text-sm text-ek-ink/65">Simulating…</p>}
    </section>
  );
};
